<template>
    <Tooltip :is-disabled="!hasTooltip" :is-teleported="true">
        <template #trigger="tooltipProps">
            <RouterLink
                :id="linkId"
                ref="linkRef"
                :to="isDisabled ? {} : props.to"
                :target="props.openInNewTab ? '_blank' : undefined"
                :aria-labelledby="
                    tooltipProps['aria-labelledby'] ? `${tooltipProps['aria-labelledby']} ${linkId}` : undefined
                "
                :popovertarget="tooltipProps['popovertarget'] ? tooltipProps.popovertarget : undefined"
                :aria-disabled="isDisabled ? true : undefined"
                class="rounded-sm flex flex-row items-center focus-visible:outline-offset-2 focus-visible:outline-2 w-max focus-visible:z-20"
                :class="[
                    variantClasses,
                    focusClasses,
                    sizeClasses,
                    { group: (hasLeftIcon || hasRightIcon) && !isDisabled },
                    isDisabled ? 'cursor-not-allowed' : 'cursor-pointer',
                ]"
                @click="handleClick"
            >
                <div v-if="hasLeftIcon" class="flex items-center" :class="[iconVariantClasses]">
                    <slot name="icon" />
                </div>

                <slot />

                <span v-if="props.status !== 'Default'">
                    <PendingIcon
                        v-if="isLoading"
                        class="animate-spin"
                        :class="{
                            'size-4': props.size === 'Extra-Small',
                            'size-4.5': props.size === 'Small',
                            'size-5': props.size === 'Base',
                        }"
                    />

                    <CheckIcon
                        v-else-if="isSuccess"
                        :class="{
                            'text-green-700 group-hover:text-green-500': isLightBackground,
                            'text-green-300 group-hover:text-green-400': isDarkBackground,
                            'size-4': props.size === 'Extra-Small',
                            'size-4.5': props.size === 'Small',
                            'size-5': props.size === 'Base',
                        }"
                    />

                    <WarningIcon
                        v-else-if="isError"
                        :class="{
                            'text-red-700 group-hover:text-red-500': isLightBackground,
                            'text-red-300 group-hover:text-red-400': isDarkBackground,
                            'size-4': props.size === 'Extra-Small',
                            'size-4.5': props.size === 'Small',
                            'size-5': props.size === 'Base',
                        }"
                    />
                </span>
            </RouterLink>
        </template>

        <template #text>
            <div class="w-max">{{ props.statusMessage || props.disabledExplanation || props.iconDescription }}</div>
        </template>
    </Tooltip>
</template>

<script setup lang="ts">
import { nanoid } from 'nanoid';
import { computed, ref, useSlots } from 'vue';
import { RouteLocationRaw, RouterLink } from 'vue-router';
import CheckIcon from '../Icon/CheckIcon.vue';
import PendingIcon from '../Icon/PendingIcon.vue';
import WarningIcon from '../Icon/WarningIcon.vue';
import Tooltip from '../Tooltip/Tooltip.vue';
import { getFocusClasses, getIconVariantClasses, getSizeClasses, getVariantClasses } from './Button.styles';
import { ButtonSize, ButtonStatus, ButtonVariant } from './Button.types';

type LinkButtonProps = {
    size: ButtonSize;
    variant: ButtonVariant;
    to: RouteLocationRaw;
    iconDescription?: string;
    hasDarkBackground?: boolean;
    openInNewTab?: boolean;
    disabledExplanation?: string;
    status?: ButtonStatus;
    statusMessage?: string;
};

type LinkButtonEmits = {
    (e: 'click', ev: MouseEvent): void;
};

const props = withDefaults(defineProps<LinkButtonProps>(), {
    status: 'Default',
});
const emit = defineEmits<LinkButtonEmits>();
const slots = useSlots();
const linkId = nanoid();

const isLoading = computed(() => props.status === 'Loading');
const isSuccess = computed(() => props.status === 'Success');
const isError = computed(() => props.status === 'Error');
const isDisabled = computed(() => Boolean(props.disabledExplanation) || isLoading.value);

const hasLeftIcon = computed(() => Boolean(slots.icon));
const hasText = computed(() => Boolean(slots.default));
const hasRightIcon = computed(() => isLoading.value || isSuccess.value || isError.value);

const isLightBackground = computed(
    () =>
        props.variant === 'Transparent-Light' ||
        props.variant === 'Transparent' ||
        props.variant === 'Danger-Light' ||
        props.variant === 'Success-Light' ||
        props.variant === 'Info-Light' ||
        props.variant === 'Neutral-Light',
);
const isDarkBackground = computed(() => !isLightBackground.value);

const variantClasses = computed(() => getVariantClasses(isDisabled.value, props.variant));
const focusClasses = computed(() => getFocusClasses(isDisabled.value, props.variant, props.hasDarkBackground));
const sizeClasses = computed(() => getSizeClasses(props.size, hasLeftIcon.value, hasText.value, hasRightIcon.value));
const iconVariantClasses = computed(() => getIconVariantClasses(isDisabled.value, props.variant));

const hasTooltip = computed(() =>
    Boolean(
        props.disabledExplanation ||
        props.iconDescription ||
        (isLoading.value && props.statusMessage) ||
        (isSuccess.value && props.statusMessage) ||
        (isError.value && props.statusMessage),
    ),
);

function handleClick(event: MouseEvent) {
    if (props.disabledExplanation) {
        event.preventDefault();
    } else {
        emit('click', event);
    }
}

const linkRef = ref();
function focus() {
    linkRef.value.focus();
}

defineExpose({
    focus,
});
</script>
